import { MainPropertyType } from '@aloft/toolkit-shared';

const depCost = {
  title: 'Depreciated Cost Adjustments',
  subtitle:
    'Depreciated cost was used to determine the contributory value of improvements. Local replacement cost data was sourced from the Craftsman National Building Cost Manual and depreciated using the age/life method.',
  subItems: {
    'main-dwelling': {
      title: 'Main Dwelling',
      subtitle:
        "The following adjustments are based on the depreciated cost of each feature at the subject's quality and condition (cost of subject’s feature x subject’s remaining economic life). This allows for quality and condition of the comparables to be adjusted separately.",
    },
    roundingAndThresholds: {
      title: 'Rounding & Thresholds',
    },
    quality: {
      title: 'Quality',
      subtitle:
        'The quality adjustment is the difference in replacement cost if the comparable had the same cost quality as the subject. This difference is multiplied by the remaining economic life of the subject so condition can be adjusted separately.',
    },
    condition: {
      title: 'Condition',
      subtitle:
        'The condition adjustment is the difference in percent depreciation between the subject and each comparable. This is multiplied by each comparable’s cost new to calculate the dollar difference in depreciation.',
    },
    functionalDepreciation: {
      title: 'Functional Utility',
      subtitle:
        'The functional utility adjustment is the difference in functional obsolescence between the subject and each comparable.',
    },
    externalDepreciation: {
      title: 'External Obsolescence',
      subtitle:
        'The external obsolescence adjustment is the difference in external obsolescence between the subject and each comparable.',
    },
    additionalStructures: {
      title: 'Additional Structures',
      subtitle:
        'Adjustments are based on the difference between the depreciated cost of the subject’s feature compared to the depreciated cost of the comparable’s feature.',
    },
  },
};

export const COMMENTARY_TEXT = {
  'dep-cost': depCost,
  'dep-cost-with-multiplier': {
    ...depCost,
    subtitle:
      'Depreciated cost was used to determine the contributory value of improvements. Local replacement cost data was sourced from the Craftsman National Building Cost Manual, to which the appraiser applied an additional cost multiplier of {costMultiplier} to further calibrate costs for the market. Costs were depreciated using the age/life method.',
  },
  'site-extraction': {
    title: 'Site Extraction Adjustments',
    subtitle:
      "The extraction method was used to determine the opinion of the subject's site value. Adjustments are based on the difference between the subject's site value and the comparable's site value. This results in a lump sum adjustment which may be due to size, location or view.",
  },
  'date-of-sale-adjustments': {
    title: 'Date of Sale Adjustments',
    subtitle:
      'Comparable sale prices were adjusted forward to reflect market value as of the effective date, based on local price trends. Where applicable, adjustments were also made for terms of sale, concessions, and list-to-sale price trends.',
  },
  'list-to-sale': {
    title: 'List to Sale Adjustments',
    subtitle:
      'Comparable sale prices were adjusted forward to reflect market value as of the effective date, based on local price trends. Where applicable, adjustments were also made for terms of sale, concessions, and list-to-sale price trends.',
    headerText: 'List to Sale Price Ratio of Comparable Sales',
  },
  'market-condition': {
    title: 'Market Condition',
    subtitle:
      'The market condition is based on the change in sale prices of comparable properties over time.',
  },
  methodology: {
    title: 'Methodology',
  },
  'cost-approach': {
    title: 'Cost Approach',
    subtitle:
      'The Replacement Cost New (RCN) of the subject was calculated using local data from the Craftsman National Building Cost Manual. The Opinion of Site Value was determined via the extraction method. This site value accounts for the raw land and the as-is value of the site improvements (i.e., grading, drains, landscaping, utilities, and more). Note that the dollars per square foot shown below are rounded to the nearest whole cent.',
  },
  'prior-sales': {
    title: 'Prior Sales Analysis',
  },
  'market-conditions': {
    title: 'Sales Market Analysis',
  },
};

export const QUALITY_TABLE_HEADERS = [
  '',
  'Craftsman Quality',
  'Cost New at Subj. Qual.',
  '- Cost New',
  '= Difference',
  "x Subj.'s Remaining Econ. Life",
  '= Difference',
  'Adjustment',
];

export const CONDITION_TABLE_HEADERS = [
  '',
  'Effective Age / Economic Life',
  '% Depreciation (Main Dwelling)',
  'Difference in Depreciation',
  'X Cost New (Main Dwelling)',
  '= Difference',
  'Adjustment',
];

export const FUNCTIONAL_DEPRECIATION_TABLE_HEADERS = [
  '',
  'Functional Obsolesence',
  'Difference',
  '= Adjustment',
];

export const EXTERNAL_DEPRECIATION_TABLE_HEADERS = [
  '',
  'External Obsolesence',
  'Difference',
  '= Adjustment',
];

export const SITE_EXTRACTION_TABLE_HEADERS = [
  '',
  'Site | Location | View',
  'Sale Price',
  '+ Mkt Adj.',
  '- Dep Cost',
  '= Site Value',
  'Site/Value',
  '$/SF',
  'Difference',
  'Adjustment',
];

export const METHODOLOGY_TEXT = [
  'The adjustments in this appraisal were derived using the Appraisify Appraiser Toolkit which is built on the recognized methods of depreciated cost, paired sales, and site value extraction. The Craftsman National Building Cost Manual was the source of local cost data, which was compared directly with the sale prices of the comps to produce mathematically precise, market-extracted adjustments.',
  "The square footage and quality level of each improvement for the subject and each comparable was researched and mapped to building cost data from Craftsman. The improvements were depreciated using the age/life method and site values were extracted for each comparable sale. These site values represent the combined market value of site size, location, view, and site improvements (ex. grading, drains, landscaping, paving, and utilities). The subject's site value was then estimated based on the extracted site values of the comps.",
  'Site adjustments were calculated by comparing the site values of the comps against the subject (ie. Subject’s Site Value - Comp Extracted Site Value = Site Adjustment).  This results in a lump sum adjustment which may be due to size, location, view or site improvements.',
  "GLA and bath adjustments were calculated based on depreciated cost per square foot (or bath) for a dwelling of the subject's quality and condition, allowing for quality and condition to be adjusted separately. Quality adjustments were calculated based on higher/lower costs to build a dwelling at each comparable’s quality level. Condition adjustments were calculated based on the difference in percent depreciation between the subject and each comparable applied to dwelling cost new.",
  'The depreciated cost for any additional features was then compared to the depreciated cost of the subject property’s features.  This determined adjustments for basement, parking, and other improvements that might exist such as pool, shed, workshop, and/or accessory unit based on the depreciated cost (ex. Depreciated Cost of the Subject’s Garage - Depreciated Cost of the Comp Garage = Garage Adjustment).',
  'These adjustments exhaustively account for market value as observed in comparable sale prices. They are bounded by well-informed conclusions regarding site-to-value ratios, effective age and quality based on the appraiser’s experience in the particular market. This market-specific focus ensures that the appraisal reflects real-world values and trends, providing an accurate and reliable assessment.',
];

const METHODOLOGY_TEXT_MULTI_FAMILY = [
  ...METHODOLOGY_TEXT.slice(0, 3),
  "GBA and bath adjustments were calculated based on depreciated cost per square foot (or bath) for a dwelling of the subject's quality and condition, allowing for quality and condition to be adjusted separately. Quality adjustments were calculated based on higher/lower costs to build a dwelling at each comparable’s quality level. Condition adjustments were calculated based on the difference in percent depreciation between the subject and each comparable applied to dwelling cost new.",
  ...METHODOLOGY_TEXT.slice(4),
];

export function getMethodologyText(propertyType: MainPropertyType) {
  switch (propertyType) {
    case 'multiFamily':
      return METHODOLOGY_TEXT_MULTI_FAMILY;
    default:
      return METHODOLOGY_TEXT;
  }
}

export const PNG_WIDTH = 510;
export const PNG_HEIGHT = 145;

export const CRAFTSMAN_QUALITY_FOOTNOTE =
  'Note: Quality ratings from the Craftsman National Building Cost manual are used to calculate replacement costs and are independent from UAD quality ratings. Craftsman uses a quality scale of 1-6 for single family residences and 1-5 for multifamily residences and manufactured homes, where 1 represents the highest quality.';
